import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Loader2, FileSpreadsheet, FileText, Users, UserSearch } from 'lucide-react';
import { toast } from 'sonner';
import { newsFeedAPI } from '../../lib/api';
import { ReactionCounts } from './reactions';
import { exportExcel, exportEmployeePdf, reactionLabel } from './engagementExport';

const fmt = (d) => (d ? new Date(d).toLocaleString('pt-BR') : '-');

const FILTERS = {
  todos: () => true,
  visualizaram: (r) => r.visualizou,
  nao_visualizaram: (r) => !r.visualizou,
  reagiram: (r) => !!r.reacao,
  sem_reacao: (r) => !r.reacao,
};

const Stat = ({ label, value, testId }) => (
  <div className="p-3 rounded-lg border border-slate-200 bg-white" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-xl font-bold text-slate-900">{value}</p>
  </div>
);

const EmployeeTab = ({ report }) => {
  const f = report.funcionarios;
  const [filter, setFilter] = useState('todos');
  const [q, setQ] = useState('');
  const items = useMemo(
    () => f.items.filter(FILTERS[filter]).filter((r) => !q || `${r.nome} ${r.cpf || ''}`.toLowerCase().includes(q.toLowerCase())),
    [f.items, filter, q]
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Stat label="Destinatários" value={f.total_destinatarios} testId="emp-stat-destinatarios" />
        <Stat label="Visualizaram" value={f.visualizaram} testId="emp-stat-visualizaram" />
        <Stat label="Não visualizaram" value={f.nao_visualizaram} testId="emp-stat-nao-visualizaram" />
        <Stat label="Reagiram" value={f.reagiram} testId="emp-stat-reagiram" />
        <Stat label="% Visualização" value={`${f.taxa_visualizacao}%`} testId="emp-stat-taxa-visualizacao" />
        <Stat label="% Engajamento" value={`${f.taxa_engajamento}%`} testId="emp-stat-taxa-engajamento" />
      </div>
      <ReactionCounts reacoes={f.reacoes} testIdPrefix="emp-report-reacoes" />
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="emp-report-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="visualizaram">Visualizaram</SelectItem>
            <SelectItem value="nao_visualizaram">Não visualizaram</SelectItem>
            <SelectItem value="reagiram">Reagiram</SelectItem>
            <SelectItem value="sem_reacao">Sem reação</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Buscar nome ou CPF" value={q} onChange={(e) => setQ(e.target.value)} className="w-56" data-testid="emp-report-search" />
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportExcel(report, 'funcionarios', items)} data-testid="emp-report-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => exportEmployeePdf(report, items)} data-testid="emp-report-pdf-btn">
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>
      </div>
      <div className="max-h-80 overflow-auto border rounded-lg">
        <table className="w-full text-sm" data-testid="emp-report-table">
          <thead className="bg-slate-50 text-slate-600 text-xs sticky top-0">
            <tr>
              <th className="text-left p-2">Nome</th><th className="text-left p-2">CPF</th><th className="text-left p-2">Cargo / Setor</th>
              <th className="text-left p-2">Visualização</th><th className="text-left p-2">Reação</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.employee_id} className="border-t" data-testid={`emp-report-row-${r.employee_id}`}>
                <td className="p-2 font-medium text-slate-900">{r.nome}</td>
                <td className="p-2 text-slate-600">{r.cpf || '-'}</td>
                <td className="p-2 text-slate-600">{r.cargo || '-'}{r.setor ? ` / ${r.setor}` : ''}</td>
                <td className={`p-2 ${r.visualizou ? 'text-emerald-700' : 'text-red-600'}`}>{r.visualizou ? fmt(r.data_leitura) : 'Não visualizou'}</td>
                <td className="p-2 text-slate-700">{r.reacao ? `${reactionLabel(r.reacao)} · ${fmt(r.data_reacao)}` : '-'}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">Nenhum registro</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CandidateTab = ({ report }) => {
  const c = report.candidatos;
  const visible = ['todos', 'candidatos'].includes(report.comunicado.publico);
  if (!visible) return <p className="py-8 text-center text-slate-500" data-testid="cand-report-na">Este comunicado não foi publicado para candidatos.</p>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Visualizaram" value={c.visualizaram} testId="cand-stat-visualizaram" />
        <Stat label="Reagiram" value={c.reagiram} testId="cand-stat-reagiram" />
        <Stat label="% Engajamento" value={`${c.taxa_engajamento}%`} testId="cand-stat-taxa-engajamento" />
      </div>
      <div className="flex items-center justify-between">
        <ReactionCounts reacoes={c.reacoes} testIdPrefix="cand-report-reacoes" />
        <Button variant="outline" size="sm" onClick={() => exportExcel(report, 'candidatos', c.items)} data-testid="cand-report-excel-btn">
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
        </Button>
      </div>
      <div className="max-h-72 overflow-auto border rounded-lg divide-y" data-testid="cand-report-list">
        {c.items.map((r) => (
          <div key={r.candidate_id} className="p-2 flex items-center justify-between text-sm" data-testid={`cand-report-row-${r.candidate_id}`}>
            <div>
              <p className="font-medium text-slate-900">{r.nome}</p>
              <p className="text-xs text-slate-500">Visualizou: {fmt(r.data_leitura)}</p>
            </div>
            <span className="text-xs text-slate-700">{r.reacao ? reactionLabel(r.reacao) : '-'}</span>
          </div>
        ))}
        {c.items.length === 0 && <p className="p-6 text-center text-slate-500">Nenhum candidato interagiu</p>}
      </div>
      <p className="text-[11px] text-slate-500">Engajamento de candidatos = reações ÷ visualizações.</p>
    </div>
  );
};

export const EngagementReportDialog = ({ newsId, onClose }) => {
  const [report, setReport] = useState(null);
  useEffect(() => {
    if (!newsId) { setReport(null); return; }
    newsFeedAPI.getEngagementReport(newsId)
      .then((r) => setReport(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || 'Erro ao gerar relatório'); onClose(); });
  }, [newsId, onClose]);

  return (
    <Dialog open={!!newsId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="engagement-report-dialog">
        <DialogHeader>
          <DialogTitle>Relatório de Engajamento</DialogTitle>
          <DialogDescription>{report?.comunicado.titulo || 'Carregando...'}</DialogDescription>
        </DialogHeader>
        {!report ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
        ) : (
          <Tabs defaultValue="funcionarios">
            <TabsList>
              <TabsTrigger value="funcionarios" data-testid="report-tab-funcionarios"><Users className="w-4 h-4 mr-1" /> Funcionários</TabsTrigger>
              <TabsTrigger value="candidatos" data-testid="report-tab-candidatos"><UserSearch className="w-4 h-4 mr-1" /> Candidatos</TabsTrigger>
            </TabsList>
            <TabsContent value="funcionarios"><EmployeeTab report={report} /></TabsContent>
            <TabsContent value="candidatos"><CandidateTab report={report} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
